/**
 * Team fees as the team page edits them, and back. A marketplace advertiser
 * team carries default fees its line items start with: a tech fee, a creative
 * tag fee, supplementary fees, fees per targeting type and fees per creative
 * format. Fixed amounts are quoted once per network currency.
 */

import type { Fee } from '@/services/earnings/fees'
import type { CurrencyAmount } from '@/services/lineItems/lineItemsApi'
import type { TeamInput, TeamLayoutFee } from '@/services/users/teamsApi'

/** A fee is charged as a fixed amount per 1000 impressions, or a share of the CPM. */
export type FeeUnit = 'MONETARY' | 'PERCENTAGE'

/** Amounts as typed, keyed by currency, so a half-typed value survives. */
export type AmountDrafts = Record<string, string>

export interface FeeDraft {
  unit: FeeUnit
  percentage: string
  amounts: AmountDrafts
}

export interface NamedFeeDraft extends FeeDraft {
  /** Stable across adds and removes, so a row keeps its inputs. */
  key: number
  name: string
}

/** One fee shared by every targeting type in the row; the API holds one fee per type. */
export interface TargetingFeeDraft extends FeeDraft {
  key: number
  /** The targeting types' API names, e.g. `SEMANTIC_CATEGORY`. */
  apiTypes: string[]
}

export interface LayoutRef {
  id: string
  name: string
}

/** One fee shared by every layout in the row; the API holds one fee per layout. */
export interface LayoutFeeDraft {
  key: number
  layouts: LayoutRef[]
  amounts: AmountDrafts
}

export interface TeamFeesDraft {
  techFee: FeeDraft
  creativeTagFee: AmountDrafts
  supplementaryFees: NamedFeeDraft[]
  targetingFees: TargetingFeeDraft[]
  layoutFees: LayoutFeeDraft[]
}

let nextKey = 1
export const newFeeKey = () => nextKey++

/** The team types that carry fees at all. */
export const FEE_TEAM_TYPES = ['AX_ADVERTISER', 'CREATIVE_AGENCY', 'SUI']
/** The team types that carry fees beyond the tech fee. */
export const FULL_FEE_TEAM_TYPES = ['AX_ADVERTISER', 'CREATIVE_AGENCY']

function amountsToDraft(fee: Fee | null | undefined): AmountDrafts {
  const drafts: AmountDrafts = {}
  const amounts = [
    ...(fee?.multiCurrencyMonetaryAmounts ?? []),
    ...(fee?.monetary ? [fee.monetary] : []),
  ]
  for (const amount of amounts) {
    if (
      amount?.currency &&
      drafts[amount.currency] === undefined &&
      Number.isFinite(amount.amount)
    ) {
      drafts[amount.currency] = String(amount.amount)
    }
  }
  return drafts
}

export function feeToDraft(fee: Fee | null | undefined, defaultUnit: FeeUnit): FeeDraft {
  const unit: FeeUnit = !fee ? defaultUnit : fee.type === 'PERCENTAGE' ? 'PERCENTAGE' : 'MONETARY'
  return {
    unit,
    percentage:
      fee?.percentage !== undefined && fee.percentage !== null ? String(fee.percentage) : '',
    amounts: amountsToDraft(fee),
  }
}

export const emptyFeeDraft = (unit: FeeUnit): FeeDraft => ({ unit, percentage: '', amounts: {} })

function parseAmount(value: string | undefined): number | null {
  if (value === undefined || value.trim() === '') return null
  const amount = Number(value)
  return Number.isFinite(amount) ? amount : null
}

function draftToAmounts(amounts: AmountDrafts): CurrencyAmount[] {
  return Object.entries(amounts).flatMap(([currency, value]) => {
    const amount = parseAmount(value)
    return amount === null ? [] : [{ currency, amount }]
  })
}

function monetaryFee(amounts: AmountDrafts): Fee | null {
  const multiCurrencyMonetaryAmounts = draftToAmounts(amounts)
  return multiCurrencyMonetaryAmounts.length
    ? { type: 'MULTI_CURRENCY_MONETARY', multiCurrencyMonetaryAmounts }
    : null
}

/** The fee the draft describes, or null when nothing has been filled in. */
export function draftToFee(draft: FeeDraft): Fee | null {
  if (draft.unit === 'PERCENTAGE') {
    const percentage = parseAmount(draft.percentage)
    return percentage === null ? null : { type: 'PERCENTAGE', percentage }
  }
  return monetaryFee(draft.amounts)
}

/** Equal for fees that charge the same, so rows of equal fees can be merged. */
function feeSignature(fee: Fee | null | undefined, defaultUnit: FeeUnit): string {
  const draft = feeToDraft(fee, defaultUnit)
  if (draft.unit === 'PERCENTAGE') return `%${parseAmount(draft.percentage)}`
  return JSON.stringify(
    draftToAmounts(draft.amounts).sort((a, b) => a.currency.localeCompare(b.currency))
  )
}

/** Items with the same fee signature, together, in the order each fee first appears. */
function groupByFee<T>(items: T[], signature: (item: T) => string): T[][] {
  const groups = new Map<string, T[]>()
  for (const item of items) {
    const sig = signature(item)
    groups.set(sig, [...(groups.get(sig) ?? []), item])
  }
  return [...groups.values()]
}

export function teamFeesToDraft(
  team: {
    techFeeCpm?: Fee | null
    creativeTagTechFeeCpm?: Fee | null
    supplementaryFees?: Fee[]
    targetingTypeTechFees?: Record<string, Fee>
    layoutFees?: TeamLayoutFee[]
  } | null
): TeamFeesDraft {
  return {
    techFee: feeToDraft(team?.techFeeCpm, 'PERCENTAGE'),
    creativeTagFee: amountsToDraft(team?.creativeTagTechFeeCpm),
    supplementaryFees: (team?.supplementaryFees ?? []).map((fee) => ({
      ...feeToDraft(fee, 'PERCENTAGE'),
      key: newFeeKey(),
      name: fee.name ?? '',
    })),
    targetingFees: groupByFee(Object.entries(team?.targetingTypeTechFees ?? {}), ([, fee]) =>
      feeSignature(fee, 'MONETARY')
    ).map((group) => ({
      ...feeToDraft(group[0][1], 'MONETARY'),
      key: newFeeKey(),
      apiTypes: group.map(([apiType]) => apiType),
    })),
    layoutFees: groupByFee(
      (team?.layoutFees ?? []).filter((layoutFee) => layoutFee.layout?.id),
      (layoutFee) => feeSignature(layoutFee.fee, 'MONETARY')
    ).map((group) => ({
      key: newFeeKey(),
      layouts: group.map((layoutFee) => ({
        id: layoutFee.layout.id,
        name: layoutFee.layout.name ?? '',
      })),
      amounts: amountsToDraft(group[0].fee),
    })),
  }
}

/**
 * The fee fields of a team save. Rows left incomplete — no name, no type, no
 * amount — are dropped, as the old UI does. A row shared by several layouts or
 * targeting types becomes one fee for each. Fields a team type does not carry
 * are left out; the API ignores them anyway.
 */
export function draftToTeamFees(draft: TeamFeesDraft, teamType: string): Partial<TeamInput> {
  if (!FEE_TEAM_TYPES.includes(teamType)) return {}
  const fees: Partial<TeamInput> = { techFeeCpm: draftToFee(draft.techFee) }
  if (!FULL_FEE_TEAM_TYPES.includes(teamType)) return fees

  fees.creativeTagTechFeeCpm = monetaryFee(draft.creativeTagFee)
  fees.supplementaryFees = draft.supplementaryFees.flatMap((row) => {
    const fee = draftToFee(row)
    return row.name.trim() && fee ? [{ ...fee, name: row.name.trim() }] : []
  })
  fees.targetingTypeTechFees = Object.fromEntries(
    draft.targetingFees.flatMap((row) => {
      const fee = draftToFee(row)
      // A zero percentage charges nothing, so it is not worth keeping.
      if (!fee || (fee.type === 'PERCENTAGE' && !fee.percentage)) return []
      return row.apiTypes.map((apiType) => [apiType, fee])
    })
  )
  fees.layoutFees = draft.layoutFees.flatMap((row) => {
    const fee = monetaryFee(row.amounts)
    return fee ? row.layouts.map((layout) => ({ layout: { id: layout.id }, fee })) : []
  })
  return fees
}

/** The fee fields of a team, as the API holds them. */
export type TeamFeeFields = Pick<
  TeamInput,
  'techFeeCpm' | 'creativeTagTechFeeCpm' | 'supplementaryFees' | 'targetingTypeTechFees' | 'layoutFees'
>

/**
 * A named set of team fees, kept network-wide in the network profile
 * (`adnDefaults.feeTemplates`), that a super admin can start a team's fees
 * from. Fees are stored as the API shapes them; layouts carry their name too,
 * so a template reads without loading every layout.
 */
export interface FeeTemplate {
  id: string
  name: string
  fees: TeamFeeFields
}

export interface FeeTemplateDraft {
  key: number
  id: string
  name: string
  fees: TeamFeesDraft
}

/** The fee templates in the network profile's defaults, skipping any malformed entry. */
export function parseFeeTemplates(defaults: Record<string, unknown>): FeeTemplate[] {
  const value = defaults.feeTemplates
  if (!Array.isArray(value)) return []
  return value.filter(
    (t): t is FeeTemplate =>
      !!t &&
      typeof t === 'object' &&
      typeof t.id === 'string' &&
      typeof t.name === 'string' &&
      !!t.fees &&
      typeof t.fees === 'object'
  )
}

export const feeTemplateToDraft = (template: FeeTemplate): FeeTemplateDraft => ({
  key: newFeeKey(),
  id: template.id,
  name: template.name,
  fees: teamFeesToDraft(template.fees),
})

export const newFeeTemplateDraft = (): FeeTemplateDraft => ({
  key: newFeeKey(),
  id: crypto.randomUUID(),
  name: '',
  fees: teamFeesToDraft(null),
})

/** The template to store, or null when it has no name. Templates carry every fee field. */
export function draftToFeeTemplate(draft: FeeTemplateDraft): FeeTemplate | null {
  const name = draft.name.trim()
  if (!name) return null
  const fees = draftToTeamFees(draft.fees, FULL_FEE_TEAM_TYPES[0]) as TeamFeeFields
  const names = new Map(
    draft.fees.layoutFees.flatMap((row) => row.layouts.map((l) => [l.id, l.name] as const))
  )
  fees.layoutFees = fees.layoutFees?.map((layoutFee) => ({
    ...layoutFee,
    layout: { id: layoutFee.layout.id, name: names.get(layoutFee.layout.id) || undefined },
  }))
  return { id: draft.id, name, fees }
}
